import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { dateTimeFormat } from '../../../../util/GlobalConstant';
import GC from '../../../../util/GlobalConstant';
import Dropdown from '../../../../components/Dropdown';
import CustomerBarChart from '../../../../components/CustomerBarChart';

import style from './index.scss';

/**
 * NoDataPanel
 * @param onTogglePanelVisibility
 * @param onReload
 * @param selectedSegment
 * @param segmentList
 * @param onSegmentChange
 * @returns {*}
 * @constructor
 */
const NoDataPanel = ({
  onTogglePanelVisibility,
  onReload,
  selectedSegment,
  segmentList,
  onSegmentChange
}) => {
  const allSegmentOption = {
    code: '-',
    id: '-',
    name: 'All Segments'
  };
  const segmentOptions = [allSegmentOption];
  if (Array.isArray(segmentList) && segmentList.length > 0) {
    segmentList.forEach((segment) => {
      segmentOptions.push({
        code: segment.code,
        id: segment.code,
        name: segment.description
      });
    });
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Customer View for {selectedSegment.name}</p>
          </div>

          <div className="col-md-4">
            <p className="text-right">
              <Dropdown
                dropdownValue={selectedSegment}
                dropdownOptions={segmentOptions}
                handleValueChange={onSegmentChange}
              />
            </p>
          </div>

          <div className="col-md-2">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>No Customer View data exists for the period selected.</p>
            <p>
              <a className={style.retryLink} onClick={onReload}>
                Click here to fetch customer view data again.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * IsLoadingPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const IsLoadingPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Customer View</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>Loading Customer View chart data...</p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * MinimizedPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const MinimizedPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Customer View</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-maximize"></i>
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * CustomerViewChart
 * @param props
 * @returns {*}
 * @constructor
 */
export const CustomerViewChart = (props) => {
  const {
    data,
    segmentList,
    selectedSegment,
    isLoading,
    isCollapsed,
    onTogglePanelVisibility,
    onReload,
    onSegmentChange
  } = props;

  if (isCollapsed) {
    return <MinimizedPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (isLoading) {
    return <IsLoadingPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (Array.isArray(data) && data.length > 0) {
    const allSegmentOption = {
      code: '-',
      id: '-',
      name: 'All Segments'
    };
    const segmentOptions = [allSegmentOption];
    Array.isArray(segmentList) &&
      segmentList.forEach((segment) => {
        segmentOptions.push({
          code: segment.code,
          id: segment.code,
          name: segment.description
        });
      });

    const formattedData = data.slice(0, 20).map((o) => {
      const currentCustomerData = o.customer;

      // return formatted data for individual customers
      if (currentCustomerData.alertType === 0) {
        return {
          ...o,
          customer: {
            ...currentCustomerData,
            alertDate: moment
              .utc(currentCustomerData.alertDate, [dateTimeFormat])
              .local()
              .format('DD/MM/YYYY'),
            dobCustomer: moment
              .utc(currentCustomerData.dobCustomer, [dateTimeFormat])
              .local()
              .format('DD/MM/YYYY'),
            nsAlertClosureDate: moment
              .utc(currentCustomerData.nsAlertClosureDate, [dateTimeFormat])
              .local()
              .format('DD/MM/YYYY')
          }
        };
      }

      // return formatted data for corporate customers
      if (currentCustomerData.alertType === 1) {
        return {
          ...o,
          customer: {
            ...currentCustomerData,
            alertDate: moment
              .utc(currentCustomerData.alertDate, [dateTimeFormat])
              .local()
              .format('DD/MM/YYYY'),
            nsAlertClosureDate: moment
              .utc(currentCustomerData.nsAlertClosureDate, [dateTimeFormat])
              .local()
              .format('DD/MM/YYYY')
          }
        };
      }

      return o;
    });

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>Customer View for {selectedSegment.name}</p>
            </div>

            <div className="col-md-4">
              <p className="text-right">
                <Dropdown
                  dropdownValue={selectedSegment}
                  dropdownOptions={segmentOptions}
                  handleValueChange={onSegmentChange}
                />
              </p>
            </div>

            <div className="col-md-2">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              <CustomerBarChart
                isNs={false}
                fill={GC.customerViewBarChartColor}
                data={formattedData}
                dataKey={'alertCount'}
                xDataKey={'name'}
                yLabel="Alert Count"
                width={innerWidth / 1.2}
                height={innerWidth / 6}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NoDataPanel
      segmentList={segmentList}
      selectedSegment={selectedSegment}
      onReload={onReload}
      onTogglePanelVisibility={onTogglePanelVisibility}
      onSegmentChange={onSegmentChange}
    />
  );
};

CustomerViewChart.propTypes = {
  data: PropTypes.array,
  segmentList: PropTypes.array,
  selectedSegment: PropTypes.object,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func,
  onSegmentChange: PropTypes.func
};
